import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { supabase } from "../../supabaseClient";
import { divisionLabelForProduct } from "../../utils/productLabels";
import { splitProductName, toProductMarkdown } from "../../utils/productDescription";

/** Known hubs first; any other cities present in products follow alphabetically. */
const CITY_ORDER = ["Mississauga", "Cambridge", "Kitchener", "Waterloo"];
const LOW_SPOTS = 3;

export const sortCityList = (list) =>
  [...list].sort((a, b) => {
    const ia = CITY_ORDER.indexOf(a);
    const ib = CITY_ORDER.indexOf(b);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.localeCompare(b);
  });

const normalizeNumber = (v) => {
  if (v == null) return null;
  const cleaned = String(v).replace(/[^0-9.]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

const money = (v) => {
  const n = normalizeNumber(v);
  return n == null ? null : `$${n.toLocaleString("en-CA")}`;
};

/** Everything a card needs to know about one product. */
export function programInfo(product, paidCountsByKey) {
  const city = String(product?.city ?? "").trim();
  const divisionLabel = divisionLabelForProduct(city, product?.division, product);
  const paidCount = paidCountsByKey[`product__${String(product.id)}`] ?? paidCountsByKey[`${city}__${divisionLabel}`] ?? 0;
  const capacity = normalizeNumber(product?.capacity);
  const remaining = capacity == null ? null : Math.max(0, Math.floor(capacity) - paidCount);
  const isSoldOut = product?.sold_out === true || (remaining != null && remaining <= 0);
  const teamOk = product?.team_registeration === true || product?.team_registration === true;
  const individualOk = product?.individual_registeration === true || product?.individual_registration === true;
  const prices = [];
  const teamPrice = money(product?.cost);
  const individualPrice = money(product?.individual_cost);
  if (teamOk && teamPrice) prices.push(`${teamPrice} / team`);
  if (individualOk && individualPrice) prices.push(`${individualPrice} / player`);
  if (!prices.length && (teamPrice || individualPrice)) prices.push(teamPrice || individualPrice);
  const { title, subtitle } = splitProductName(product?.name);
  return {
    product,
    city,
    divisionLabel,
    title: title || `${city} – ${divisionLabel}`,
    subtitle,
    isSoldOut,
    remaining,
    regLabel: teamOk && individualOk ? "Teams & individuals" : teamOk ? "Teams" : individualOk ? "Individuals" : null,
    prices,
    timing: product?.timing?.length ? product.timing.join(" · ") : null,
    location: product?.location || null,
    markdown: toProductMarkdown(product?.description),
    sport: [product?.sport, product?.age_group].filter(Boolean).join(" · "),
  };
}

export function StatusBadge({ info, sx }) {
  const { isSoldOut, remaining } = info;
  const low = !isSoldOut && remaining != null && remaining <= LOW_SPOTS;
  const label = isSoldOut ? "Sold out" : low ? `${remaining} spot${remaining === 1 ? "" : "s"} left` : "Open";
  const colors = isSoldOut
    ? { bg: "rgba(17,17,17,0.82)", fg: "#fff" }
    : low
      ? { bg: "#fff4e5", fg: "#9a3412" }
      : { bg: "#e8f7ee", fg: "#166534" };
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        borderRadius: 999,
        fontSize: "0.75rem",
        fontWeight: 800,
        letterSpacing: 0.3,
        bgcolor: colors.bg,
        color: colors.fg,
        ...sx,
      }}
    >
      {!isSoldOut && <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "currentColor" }} />}
      {label}
    </Box>
  );
}

export function FeaturedSlide({ info, onSelect }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      sx={{
        all: "unset",
        boxSizing: "border-box",
        cursor: "pointer",
        position: "relative",
        display: "block",
        width: "100%",
        aspectRatio: { xs: "4 / 5", sm: "16 / 10" },
        borderRadius: 4,
        overflow: "hidden",
        bgcolor: "#111",
        color: "#fff",
        "&:hover img": { transform: "scale(1.03)" },
        "&:focus-visible": { outline: "3px solid #111", outlineOffset: 3 },
      }}
    >
      {info.product?.image && (
        <Box
          component="img"
          src={info.product.image}
          alt=""
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 30%", transition: "transform 0.4s ease" }}
        />
      )}
      <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.85) 100%)" }} />
      <StatusBadge info={info} sx={{ position: "absolute", top: 14, left: 14 }} />
      <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 0, p: { xs: 2.25, sm: 3 } }}>
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase", opacity: 0.8 }}>
          {[info.city, info.subtitle].filter(Boolean).join(" · ")}
        </Typography>
        <Typography sx={{ fontSize: { xs: "1.5rem", sm: "1.9rem" }, fontWeight: 900, lineHeight: 1.1, mt: 0.5, letterSpacing: "-0.01em" }}>
          {info.title}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mt: 1.5 }}>
          {info.prices.map((p) => (
            <Box key={p} component="span" sx={{ fontWeight: 800, fontSize: "0.9rem", px: 1.25, py: 0.5, borderRadius: 999, bgcolor: "rgba(255,255,255,0.16)", backdropFilter: "blur(6px)" }}>
              {p}
            </Box>
          ))}
          <Box component="span" sx={{ ml: "auto", fontWeight: 800, fontSize: "0.9rem", px: 2, py: 0.9, borderRadius: 999, bgcolor: "#fff", color: "#111" }}>
            Register →
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/** Products plus paid-registration counts (for "N spots left" / sold out). */
export function useRegistrationProducts() {
  const [products, setProducts] = useState(null);
  const [paidCountsByKey, setPaidCountsByKey] = useState({});

  useEffect(() => {
    let cancelled = false;
    supabase
      .from("products")
      .select(
        "id, city, division, capacity, cost, individual_cost, location, timing, description, name, image, sport, age_group, team_registeration, individual_registeration, individual_signup_questions, product_type, captain_only_registration, require_email_consent, sold_out"
      )
      .then(({ data }) => !cancelled && setProducts(data || []));
    supabase
      .from("league_teams")
      .select("region, division, product_id")
      .eq("payment_status", "paid")
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          setPaidCountsByKey({});
          return;
        }
        const counts = {};
        for (const row of data || []) {
          if (row?.product_id != null && String(row.product_id).trim()) {
            const key = `product__${String(row.product_id)}`;
            counts[key] = (counts[key] || 0) + 1;
            continue;
          }
          const region = row?.region || "";
          const key = `${region}__${divisionLabelForProduct(region, row?.division)}`;
          counts[key] = (counts[key] || 0) + 1;
        }
        setPaidCountsByKey(counts);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { products, paidCountsByKey };
}

export function openRegistration(navigate, info) {
  navigate("/register/apply", { state: { region: info.city, division: info.divisionLabel, productId: info.product?.id } });
  requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: "auto" }));
}
